import DataTable from "@/components/DataTable";
import { DataTableColumnDefinition, DataTableRow, DataType } from "@/components/DataTable/Models";
import { UseCase } from "@/services/global-services/models";
import { useDataStore, useFilterStore } from "@/stores";
import { useCallback, useEffect, useState } from "react";
import { BlockTableViewerProps, DataTableTemplateConfig } from "../../Models";

const BlockTableViewer = ({ data, config }: BlockTableViewerProps) => {
    const software = useDataStore((state) => state.software);
    const useCases = useDataStore((state) => state.useCases);
    const classifications = useDataStore((state) => state.classifications);

    const activeVersionName = useFilterStore((state) => state.activeVersion)!;
    const activeDisciplines = useFilterStore((state) => state.activeDisciplines);
    const activeSoftware = useFilterStore((state) => state.activeSoftware);
    const activeIfcVersion = useFilterStore((state) => state.activeIfcVersion);
    const activeUseCases = useFilterStore((state) => state.activeUseCases);
    const activeClassification = useFilterStore((state) => state.activeClassification);

    const [tableConfig, setTableConfig] = useState<DataTableTemplateConfig>();

    useEffect(() => {
        if (software[activeVersionName].length === 0) return;

        if (config.hasSoftwareColumns || config.hasClassificationColumns) {
            const data = { ...config };
            if (config.hasSoftwareColumns) {
                const sorted = software[activeVersionName]
                    .filter((x) => Boolean(x.active))
                    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name));

                for (let i = 0; i < sorted.length; i++) {
                    if (data.columns.findIndex((x) => x.value === sorted[i].name) === -1)
                        data.columns.push({
                            value: sorted[i].name,
                            dataType: DataType.STRING,
                            metadata: { softwareId: sorted[i].id },
                        });
                }
            }

            if (config.hasClassificationColumns && classifications) {
                const sorted = classifications[activeVersionName]
                    .filter((x) => Boolean(x.active))
                    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.name ?? "").localeCompare(b.name ?? ""));

                for (let i = 0; i < sorted.length; i++) {
                    if (data.columns.findIndex((x) => x.value === sorted[i].name) === -1)
                        data.columns.push({
                            value: sorted[i].name,
                            dataType: DataType.STRING,
                            metadata: { classificationId: sorted[i].id },
                        });
                }
            }

            setTableConfig(data);
        } else setTableConfig(config);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [software, classifications]);

    const filterColumn = useCallback(
        (column: DataTableColumnDefinition) => {
            if (column.metadata?.softwareId || column.metadata?.classificationId) {
                if (column.metadata?.softwareId) {
                    const activeSoftwareIds = [activeSoftware, activeIfcVersion].flat().filter(Boolean) as string[];
                    return activeSoftwareIds.indexOf(column.metadata.softwareId as string) === -1;
                } else if (column.metadata?.classificationId) {
                    return activeClassification !== column.metadata.classificationId;
                }
            }

            return false;
        },
        [activeSoftware, activeIfcVersion, activeClassification]
    );

    const filterRow = useCallback(
        (row: DataTableRow) => {
            if (activeDisciplines.length > 0) {
                if (row.metadata) {
                    const rowDisciplines = row.metadata.disciplines as Array<string>;
                    if (rowDisciplines && rowDisciplines.length > 0) {
                        return !rowDisciplines.some((x) => activeDisciplines.includes(x));
                    }
                }
            }

            return false;
        },
        [activeDisciplines]
    );

    const filterUseCase = useCallback(
        (useCase: UseCase) => {
            if (activeUseCases.length === 0) return false;

            if (useCase.attachedDiscipline === undefined) {
                return activeUseCases.indexOf(useCase.id) === -1;
            } else {
                if (useCase.attachedDiscipline?.some((disciplineId) => activeDisciplines.includes(disciplineId)))
                    return activeUseCases.indexOf(useCase.id) === -1;
            }

            return true;
        },
        [activeUseCases]
    );

    return tableConfig ? (
        <DataTable
            config={{ ...tableConfig, useCases: useCases[activeVersionName] }}
            data={data}
            activeDisciplines={activeDisciplines}
            readOnly
            onFilterColumn={filterColumn}
            onFilterRow={filterRow}
            onFilterUseCase={filterUseCase}
        />
    ) : null;
};

export default BlockTableViewer;
